import { toAttendee } from '@tunasong/calendar'
import { shortUuid } from '@tunasong/models'
import { getDefaultYDocString } from '@tunasong/plugin-lib'
import type { Calendar, CalendarEvent, ManyOfManyPoll, Persisted, PollResponse, Profile } from '@tunasong/schemas'
import { isDateSlot } from '@tunasong/schemas'
import { getLiveSlotIndices, getSlotEnd } from './date-slot.js'

export interface BuildCalendarEventsArgs {
  poll: Persisted<ManyOfManyPoll>
  responses: Persisted<PollResponse>[]
  members: Profile[]
  organizer: Profile
}

/**
 * One `calendarevent` per winning slot, with the yes-voters as attendees.
 *
 * Slots store `z.iso.datetime()`, the same shape as `calendarEventSchema.start`, so this is a
 * field copy rather than a conversion. A maybe is not an attendance and never becomes an
 * attendee.
 */
export const buildCalendarEvents = ({ poll, responses, members, organizer }: BuildCalendarEventsArgs) => {
  /** Via getLiveSlotIndices, so a tombstoned slot cannot become an event even if marked final. */
  const winners = getLiveSlotIndices(poll).filter(idx => poll.finalOption?.[idx])

  return winners.reduce<CalendarEvent[]>((events, idx) => {
    const slot = poll.pollOptions[idx]
    if (!isDateSlot(slot)) {
      return events
    }

    const attendees = responses
      .filter(response => response.availability?.[String(idx)] === 'yes')
      .map(response => members.find(member => member.userId === response.userId))
      .filter((member): member is Profile => Boolean(member))
      .map(toAttendee)

    return [
      ...events,
      {
        // Set client-side so the calendar updates optimistically.
        id: shortUuid(),
        type: 'calendarevent',
        name: poll.name,
        start: slot.start,
        end: getSlotEnd(slot),
        organizer: toAttendee(organizer),
        attendees,
        yDoc: getDefaultYDocString(),
      } satisfies CalendarEvent,
    ]
  }, [])
}

/**
 * `all` is a claim about quantity and is false on a single-date poll — the same rule that gives
 * `Rehearsal set for X` against `3 rehearsals set`. Every string in the handoff counts the same
 * dates, so the trigger, the dialog title and the confirm have to agree; a button reading
 * `Add all to calendar` opening a dialog headed `Add to calendar` is one poll disagreeing with
 * itself about how many rehearsals it decided.
 */
const addToCalendarVerb = (count: number) => (count === 1 ? 'Add' : 'Add all')

/** The trigger on the closed page. Named for the count, never for the destination. */
export const formatAddToCalendarTrigger = (count: number) => `${addToCalendarVerb(count)} to calendar`

/** The confirm. Names the calendar when the target resolved to exactly one; generic otherwise. */
export const formatAddToCalendarLabel = (calendar: Pick<Calendar, 'name'> | undefined, count: number) =>
  `${addToCalendarVerb(count)} to ${calendar?.name || 'calendar'}`

/** `Added to Band calendar` once the handoff has completed; generic if the name has not loaded. */
export const formatAddedToCalendarLabel = (calendar?: Pick<Calendar, 'name'>) =>
  calendar?.name ? `Added to ${calendar.name}` : 'Added to calendar'
